import { events, type EventsChannel } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useRef, useState } from 'react';

import {
  TDocument,
  TUseAppsyncEventsParams,
} from '@/shared/hooks/use-appsync-events/use-app-sync-events.types';

export const useAppSyncEvents = ({
  webSocketChannel = '',
  connectOnInit = false,
  onMessageReceived,
}: TUseAppsyncEventsParams) => {
  const channel = useRef<EventsChannel>(null);
  const sub = useRef<ReturnType<EventsChannel['subscribe']>>(null);
  const [error, setError] = useState<boolean>(false);

  const connectAndSubscribe = async (
    webSocketChannel: string,
    onMessageReceived?: (value: any) => void,
    onError?: () => void,
  ) => {
    try {
      const session = await fetchAuthSession();
      const authToken = session?.tokens?.idToken?.toString() || '';

      console.debug(`Subscribing to ${webSocketChannel}`);
      channel.current = await events.connect(webSocketChannel, {
        authToken,
      });

      if (!sub.current) {
        sub.current = channel.current.subscribe({
          next: (data) => {
            if (!data) {
              return;
            }

            console.log('message received', { data });
            console.log('on channel', { webSocketChannel });
            onMessageReceived?.(data);
          },
          error: (err) => {
            console.error('error', err);
            setError(true);
            onError?.();
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const postMessage = async (message: TDocument) => {
    try {
      console.debug(`Posting message via HTTP to ${webSocketChannel}`);
      await events.post(webSocketChannel, message);
    } catch (error) {
      console.error(error);
    }
  };

  const publishMessage = async (message: TDocument) => {
    if (!channel.current) {
      console.debug('Channel is undefined');
      return;
    }

    try {
      console.debug(`Publishing message to ${webSocketChannel}`);
      await channel.current.publish(message);
    } catch (error) {
      console.error(error);
    }
  };

  const disconnect = () => {
    sub.current?.unsubscribe();
    sub.current = null;
    // Unsubscribing leaves the channel in Amplify's open-channel set, so the
    // shared WebSocket is never torn down. Only close() releases it.
    channel.current?.close();
    channel.current = null;
    setError(false);
  };

  useEffect(() => {
    if (!connectOnInit) {
      return;
    }

    void connectAndSubscribe(webSocketChannel, onMessageReceived);

    return disconnect;
  }, []);

  return {
    postMessage,
    publishMessage,
    connectAndSubscribe,
    disconnect,
    current: sub?.current,
    error,
  };
};
